import {
  BarChart3, Megaphone, Gift, CreditCard, TrendingUp, BookOpen, Building2,
  Users, ShoppingBag, Wallet, Share2, Crown, BookMarked, Plus, Sparkles, Target,
} from 'lucide-react';

export const brandOnboardingSteps = [
  {
    icon: Building2,
    title: 'Welcome to Your Brand Portal',
    description:
      'This is your central hub for managing brand campaigns, sponsoring student stories, and reaching families through educational content.',
    tips: [
      'Track impressions, clicks, and engagement from your dashboard',
      'Create and manage advertising campaigns',
      'Monitor your campaign budget and spending',
    ],
  },
  {
    icon: Megaphone,
    title: 'Create Campaigns',
    description:
      'Launch targeted ad campaigns that appear naturally within student reading stories. Your brand becomes part of engaging, age-appropriate narratives.',
    tips: [
      'Set target age groups, countries, and languages',
      'Upload your brand logo and creative assets',
      'Control daily budget and campaign duration',
    ],
  },
  {
    icon: BookOpen,
    title: 'Story Integrations',
    description:
      'See exactly how your brand appears in student stories. Review narrative excerpts, activation questions, and student engagement data.',
    tips: [
      'Real story excerpts show your brand in context',
      'View student comprehension responses about your brand',
      'Track how effectively students engage with your message',
    ],
  },
  {
    icon: Gift,
    title: 'Promotional Offers',
    description:
      'Create special offers that are pushed directly to parents and guardians. Drive conversions with platform promotions or external deals.',
    tips: [
      'Offer free trials, discounts, or exclusive content',
      'Link to external landing pages or use internal promo codes',
      'Track offer views, clicks, and dismissals',
    ],
  },
  {
    icon: TrendingUp,
    title: 'Analytics & Growth',
    description:
      'Monitor your brand performance with real-time analytics. See what works and optimize your campaigns for maximum impact.',
    tips: [
      'View impressions, click-through rates, and conversions',
      'Top up your campaign budget directly from the portal',
      'Reach families who value education and literacy',
    ],
  },
];

export const guardianOnboardingSteps = [
  {
    icon: Users,
    title: 'Welcome, Parent!',
    description:
      'This portal is your command center for managing your children\'s reading journey. Add students, track progress, and unlock powerful learning tools.',
    tips: [
      'Add and manage student profiles for each child',
      'Personalize learning with virtues, interests, and faith',
      'Monitor vocabulary growth and reading progress',
    ],
  },
  {
    icon: Plus,
    title: 'Set Up Your Student',
    description:
      'Create a student profile with their grade level, interests, strengths, and areas for growth. Our AI uses this to generate perfectly tailored stories.',
    tips: [
      'Choose virtues and life lessons for character development',
      'Set reading level for age-appropriate vocabulary',
      'Add cultural and faith-based preferences',
    ],
  },
  {
    icon: Crown,
    title: 'Subscription & Plans',
    description:
      'You start with a free plan. Upgrade anytime to unlock unlimited stories, advanced analytics, and premium features for your family.',
    tips: [
      'Free plan includes basic story generation',
      'Premium plans unlock unlimited stories and features',
      'Apply coupon codes for discounts',
    ],
  },
  {
    icon: ShoppingBag,
    title: 'Marketplace & Offers',
    description:
      'Browse the marketplace for additional learning resources. Check the Offers tab for special deals from brand partners.',
    tips: [
      'View curated offers from educational brands',
      'Toggle offers on or off in the Offers tab',
      'Access exclusive deals and promotions',
    ],
  },
  {
    icon: TrendingUp,
    title: 'Track Progress',
    description:
      'Watch your child grow! Track vocabulary mastered, stories read, and their Agentic Reach Score — a measure of how their reading expands their world.',
    tips: [
      'Vocabulary Mastered counts unique words learned',
      'Agentic Reach Score measures reading breadth',
      'Invite friends with your referral code to earn rewards',
    ],
  },
];

export const teacherOnboardingSteps = [
  {
    icon: Users,
    title: 'Welcome, Educator!',
    description:
      'This is your Educator Portal — manage classrooms, import students, track progress, and bring literacy to life with AI-powered stories.',
    tips: [
      'Create classrooms for each grade or group',
      'Bulk import students via CSV spreadsheet',
      'Invite parents to complete student profiles',
    ],
  },
  {
    icon: Plus,
    title: 'Import Your Students',
    description:
      'Upload a CSV with student names, ages, grades, and parent emails. The system creates accounts and invites parents automatically.',
    tips: [
      'Download the CSV template from the Students tab',
      'Up to 1,000 students per upload',
      'Parents receive email invitations to complete profiles',
    ],
  },
  {
    icon: BookOpen,
    title: 'Organize Classrooms',
    description:
      'Create named classrooms (3rd Grade Reading, After-School Club). Assign students and word banks to each group.',
    tips: [
      'One student can be in multiple classrooms',
      'Assign specific word banks per classroom',
      'Track analytics per classroom or school-wide',
    ],
  },
  {
    icon: Sparkles,
    title: 'Generate Stories for the Class',
    description:
      'Generate personalized stories for an entire classroom at once. Each student gets a version tailored to their interests and reading level.',
    tips: [
      'Batch generate for 50+ students at once',
      'Stories adapt to each student\'s profile',
      'Monitor completion and reading progress',
    ],
  },
  {
    icon: TrendingUp,
    title: 'Go Live & Track Progress',
    description:
      'Host live video sessions with your class via the "Go Live" button. Track enrollment, parent signups, and student reading analytics.',
    tips: [
      'Share your screen during live sessions',
      'Send reminders to parents who haven\'t signed up',
      'View class-wide analytics and leaderboards',
    ],
  },
];

export const studentOnboardingSteps = [
  {
    icon: Sparkles,
    title: 'Welcome to Your Academy!',
    description:
      'This is your personal reading space where stories are created just for you. Every story features words and topics you love!',
    tips: [
      'Stories are written at your reading level',
      'Your interests and hobbies shape each story',
      'Answer questions to earn vocabulary points',
    ],
  },
  {
    icon: BookMarked,
    title: 'Read Amazing Stories',
    description:
      'Click "New Story" to generate a personalized story. Each one includes vocabulary words to help you learn and grow as a reader.',
    tips: [
      'New stories are generated by AI just for you',
      'Highlighted words are your vocabulary targets',
      'Read at your own pace — no rush!',
    ],
  },
  {
    icon: Target,
    title: 'Answer Questions',
    description:
      'After each story, you\'ll answer comprehension questions. These help you remember new words and understand what you read.',
    tips: [
      'Questions test vocabulary understanding',
      'Getting answers right builds your score',
      'Don\'t worry about mistakes — they help you learn',
    ],
  },
  {
    icon: TrendingUp,
    title: 'Watch Yourself Grow',
    description:
      'Your dashboard shows how many words you\'ve mastered and stories you\'ve completed. Keep reading to climb the ranks!',
    tips: [
      'Track your Vocabulary Mastered count',
      'See your total stories read',
      'Join classroom sessions with your teacher\'s code',
    ],
  },
];
